import { useEffect, useRef, useState } from 'react';
import { Bot, X, Send, Loader2, Undo2, FileText } from 'lucide-react';
import MicButton from './ui/MicButton';
import { Linkified } from './CopilotPanel';
import { QuoteCard, DraftLink } from './CopilotQuoteCard';
import { api } from '../lib/api';
import { dockKey, threadFor, dockRequest, dockTurn, notSent } from '../../modules/copilot/dock-state.mjs';
import { meetingTurn } from '../../modules/copilot/meeting-review.mjs';

// What to try on a quote, one per line (Chad's form rule: choices are a vertical list).
const CHIPS = ['Make the shake $1.70', 'Change to 17,120 sf', 'Add 5% overage', 'Drop tools', 'How does it look?', 'Mark it ready for approval'];

/**
 * CopilotDock (LP-402): the Portal Copilot docked beside the quote builder (src/pages/QuoteEditorPage.jsx),
 * working on that one quote: every message carries active_quote_id, so "make the shake $1.70" changes
 * this quote. The Copilot drafts only: it never approves or sends (a partner's click; over $25,000 two
 * partners).
 *
 * The page does the form side: beforeSend() saves unsaved edits first (so the Copilot changes what is
 * on screen) and resolves to null, or to the reason the message is held without saving or sending (an
 * approved quote whose return to draft was not confirmed, LP-443); onAnswer(res, text, { failed })
 * reloads the quote and returns the change it recorded
 * ({ id, lines } or null); onUndo(id) puts that change back; undoable(id) says whether it can be undone
 * now (newest first, never over a later edit, and not while anything is saving), and undoReason(id) why
 * not, in one line shown under the button; undoNote(id), when it can, what the Undo does not put back
 * (LP-442 r2: the Undo of a change that turned the quote budgetary keeps it budgetary). A sticky column on wide screens; a drawer from the bottom on
 * phones (open / onClose).
 *
 * One thread per quote version (LP-443, modules/copilot/dock-state.mjs): each message is made with
 * dockTurn(quote), and only this version's messages are shown and sent as history; a message that was
 * not sent (and the notice saying so) never goes as history. The page also remounts the dock per
 * version (key={dockKey(q)}).
 *
 * meeting (LP-403, only when "Start from a meeting" is switched on; null otherwise, and the dock is as
 * before): { onRead({ text } | { file }) → the Copilot's summary of what it found, feed: messages the page
 * adds (an Accept's result, with its Undo), each made with dockTurn(quote) for its version }. Every message
 * from a meeting is made with meetingTurn() on its version's thread, which makes it local, so the chat
 * request (dockRequest) never carries it: the chat has tools, the meeting is data.
 */
export default function CopilotDock({ quote, open, onClose, beforeSend, onAnswer, onUndo, undoable = () => true, undoReason = () => null, undoNote = () => null, meeting = null }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [undoing, setUndoing] = useState(null);
  const [undone, setUndone] = useState({});
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const threadKey = dockKey(quote);
  const thread = threadFor(messages, threadKey);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages, loading]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  // LP-403: what the page says after a meeting's facts were accepted (with that change's Undo).
  const fed = useRef(0);
  const feed = meeting?.feed;
  useEffect(() => {
    if (!feed || feed.length <= fed.current) return;
    const fresh = feed.slice(fed.current);
    fed.current = feed.length;
    setMessages((prev) => [...prev, ...fresh.map((m) => meetingTurn(m.role, m.content, m))]);
  }, [feed]);

  // LP-403: a message from a meeting, on this version's thread (dockTurn) and local (meetingTurn): never history.
  const fromMeeting = (role, content) => meetingTurn(role, content, dockTurn(quote, role, content));

  /** LP-403: read a meeting (pasted text or a file) through the page; the review shows in the form. True once it is read. */
  async function readMeeting(input, label) {
    if (loading || !quote || !meeting) return false;
    setMessages((prev) => [...prev, fromMeeting('user', label)]);
    setLoading(true);
    try {
      const summary = await meeting.onRead(input);
      setMessages((prev) => [...prev, fromMeeting('assistant', summary)]);
      return true;
    } catch (e) {
      setMessages((prev) => [...prev, fromMeeting('assistant', `I could not read that meeting: ${e.message}`)]);
      return false;
    } finally { setLoading(false); }
  }

  async function send(textArg) {
    const text = String(textArg ?? input).trim();
    if (!text || loading || !quote) return;
    setInput('');
    // This version's thread only: nothing said about another quote or version goes with this one.
    const body = dockRequest(text, messages, quote);
    const mine = dockTurn(quote, 'user', text);
    setMessages((prev) => [...prev, mine]);
    setLoading(true);
    try {
      let held = null;
      try {
        held = await beforeSend?.();
      } catch (e) {
        setMessages((prev) => notSent(prev, mine, `Not sent: your changes could not be saved first (${e.message}). Fix them, or reload, and ask again.`, quote));
        return;
      }
      if (held) {
        // Never history: an instruction the partner did not confirm must not reach the Copilot later.
        setMessages((prev) => notSent(prev, mine, held, quote));
        setInput((cur) => cur || text); // ready to send again after Save
        return;
      }
      let res = null;
      let failed = false;
      try {
        res = await api.post('/copilot/chat', body);
      } catch (e) {
        failed = true;
        res = { answer: e.message && /not configured|partners only/i.test(e.message) ? e.message : 'Something went wrong. Please try again.', drafts: [] };
      }
      // The form follows whatever the Copilot saved on this quote (even when the answer was lost).
      let change = null;
      try { change = await onAnswer?.(res, text, { failed }); } catch { /* the page shows its own error */ }
      // This quote's card is the form itself; other drafts (another quote, the RFP, a task) show here.
      const drafts = (res.drafts || []).filter((d) => !(d.kind === 'quote' && d.id === quote.id));
      setMessages((prev) => [...prev, dockTurn(quote, 'assistant', res.answer || "I couldn't find an answer.", { drafts, change, toolsUsed: res.toolsUsed || [] })]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  async function undo(id) {
    if (undoing) return;
    setUndoing(id);
    try {
      await onUndo?.(id);
      setUndone((u) => ({ ...u, [id]: true }));
    } catch (e) {
      setMessages((prev) => [...prev, dockTurn(quote, 'assistant', `Undo did not save: ${e.message}`, { local: true })]);
    } finally { setUndoing(null); }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  return (
    <aside
      aria-label="Copilot"
      className={`fixed inset-x-0 bottom-0 z-50 flex h-[78vh] flex-col rounded-t-2xl border border-[var(--border)] bg-white shadow-2xl transition-transform duration-200 motion-reduce:transition-none dark:bg-slate-900 lg:sticky lg:top-0 lg:z-auto lg:h-[calc(100vh-7rem)] lg:max-h-[60rem] lg:translate-y-0 lg:rounded-xl lg:shadow-none ${open ? 'translate-y-0' : 'translate-y-[102%]'}`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] px-4 py-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold"><Bot size={16} className="shrink-0 text-primary" /> Copilot</p>
          <p className="truncate text-xs text-[var(--text-secondary)]">Drafts and revises {quote ? `${quote.number} V${quote.version}` : 'this quote'}. Never approves or sends.</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:text-gray-600 lg:hidden" aria-label="Close Copilot"><X size={18} /></button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3" aria-live="polite">
        {thread.length === 0 && (
          <p className="text-sm text-[var(--text-secondary)]">Tell me what to change and I fill in the form: each change is marked "Changed by Copilot" with an Undo. You can still type and click in the form. Your unsaved edits are saved before I change anything; on an approved quote, only once you confirm it goes back to draft.</p>
        )}
        {thread.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[92%] break-words rounded-xl px-3 py-2 text-sm ${m.role === 'user' ? 'rounded-br-none bg-primary text-white' : 'rounded-bl-none bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-200'}`}>
              <span className="whitespace-pre-wrap">{m.role === 'assistant' ? <Linkified text={m.content} /> : m.content}</span>
              {m.change && (() => {
                const why = !undone[m.change.id] && undoing !== m.change.id ? undoReason(m.change.id) : null;
                const note = why ? null : undoNote(m.change.id);
                return (
                  <div className="mt-2 rounded-lg border border-sky-200 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-sky-800 dark:bg-slate-900 dark:text-gray-200">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">Changed in the form</p>
                    {(m.change.lines.length ? m.change.lines : ['The highlighted fields']).map((c, j) => <p key={j}>{c}</p>)}
                    <button type="button" disabled={loading || !!undoing || undone[m.change.id] || !!why || !undoable(m.change.id)} onClick={() => undo(m.change.id)}
                      className="mt-1 inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2 disabled:text-gray-400 disabled:no-underline">
                      {undoing === m.change.id ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}{undone[m.change.id] ? 'Undone' : 'Undo'}
                    </button>
                    {why && <p className="mt-0.5 text-[11px] text-[var(--text-secondary)]">{why}</p>}
                    {note && <p className="mt-0.5 text-[11px] text-[var(--text-secondary)]">{note}</p>}
                  </div>
                );
              })()}
              {m.drafts?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {m.drafts.map((d) => {
                    if (d.kind === 'quote' && d.quote) return <QuoteCard key={`q-${d.id}`} q={d.quote} />;
                    if (d.kind === 'rfp' && d.rfp) return <div key={`r-${d.id}`} className="rounded-lg border border-[var(--border)] bg-white p-2 text-xs dark:bg-slate-900"><p className="font-medium">{d.rfp.subject}</p><pre className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap break-words font-sans">{d.rfp.body}</pre><p className="mt-1 text-[var(--text-secondary)]">Not sent.</p></div>;
                    // A link only when its href stays on this origin (LP-443); else the label as text.
                    return <DraftLink key={`${d.kind}-${d.id}`} d={d} />;
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start"><div className="flex items-center gap-2 rounded-xl rounded-bl-none bg-gray-100 px-3 py-2 text-xs text-gray-500 dark:bg-slate-800"><Loader2 size={14} className="animate-spin" /> Working on it</div></div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="space-y-2 border-t border-[var(--border)] px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {/* LP-403 "Start from a meeting" (transcript intake), above the suggestions; only when switched on. */}
        {meeting && <MeetingIntake disabled={loading || !quote} onRead={readMeeting} />}
        <div className="grid justify-items-start gap-1.5">
          {CHIPS.map((c) => (
            <button key={c} type="button" disabled={loading || !quote} onClick={() => send(c)} className="rounded-lg border border-[var(--border)] px-2.5 py-1 text-left text-xs hover:border-primary hover:text-primary disabled:opacity-50">{c}</button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <textarea ref={inputRef} rows={1} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Tell Copilot what to change" aria-label="Message Copilot"
            className="max-h-24 min-w-0 flex-1 resize-none overflow-y-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 dark:border-slate-600 dark:bg-slate-800 sm:text-sm" style={{ minHeight: '40px' }} />
          <MicButton onTranscript={(t) => setInput((p) => (p ? `${p} ${t}` : t).trim())} size={16} />
          <button type="button" onClick={() => send()} disabled={loading || !input.trim() || !quote} className="rounded-lg bg-primary p-2.5 text-white hover:bg-primary/90 disabled:opacity-40" aria-label="Send"><Send size={16} /></button>
        </div>
      </div>
    </aside>
  );
}

/**
 * LP-403 "Start from a meeting": drop or pick a transcript (.txt, .docx, .pdf), or paste it. The Copilot
 * reads it as data only; what it finds shows in the form for a partner to accept.
 */
function MeetingIntake({ disabled, onRead }) {
  const [text, setText] = useState('');
  const [over, setOver] = useState(false);
  const box = useRef(null);
  // Folded away once a meeting is sent to be read (the review shows in the form).
  const read = (input, label) => { if (box.current) box.current.open = false; return onRead(input, label); };
  // Restore a paste that was not read, unless the partner has already typed a replacement.
  // Open the intake again so the restored transcript is visible and ready to trim or retry.
  const readPasted = async () => {
    const t = text;
    const n = t.split(/\r?\n/).filter((l) => l.trim()).length;
    setText('');
    if (await read({ text: t }, `Start from this meeting (${n} ${n === 1 ? 'line' : 'lines'})`)) return;
    setText((cur) => cur || t);
    if (box.current) box.current.open = true;
  };
  const pick = (file) => {
    if (!file || disabled) return;
    if (!/\.(txt|docx|pdf)$/i.test(file.name)) { read({ bad: true }, `Start from ${file.name}`); return; }
    read({ file }, `Start from this meeting: ${file.name}`);
  };
  return (
    <details ref={box} className="rounded-lg border border-[var(--border)] px-2.5 py-1.5">
      <summary className="cursor-pointer text-sm font-semibold">Start from a meeting</summary>
      <div className="mt-2 grid gap-2">
        <label
          className={`grid cursor-pointer gap-0.5 rounded-lg border-[1.5px] border-dashed px-2 py-2 text-center text-xs ${over ? 'border-primary bg-gray-50 dark:bg-slate-800' : 'border-[var(--border)]'}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]); }}>
          <span className="flex items-center justify-center gap-1"><FileText size={13} /> Drop a transcript here, or click to pick a file</span>
          <span className="text-[11px] text-[var(--text-secondary)]">.txt, .docx or .pdf</span>
          <input type="file" accept=".txt,.docx,.pdf,text/plain" className="sr-only" disabled={disabled} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Or paste the transcript here" aria-label="Paste a meeting transcript"
          className="w-full resize-y rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-base dark:border-slate-600 dark:bg-slate-800 sm:text-sm" />
        <div className="grid justify-items-start gap-1.5">
          <button type="button" disabled={disabled || !text.trim()} onClick={readPasted}
            className="rounded-lg bg-primary px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40">Read pasted text</button>
          <button type="button" disabled className="rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs opacity-50" title="Coming later">Pick a Plaud recording (coming later)</button>
        </div>
        <p className="text-[11px] text-[var(--text-secondary)]">Copilot reads the meeting as data only. Nothing goes into the quote until you accept it.</p>
      </div>
    </details>
  );
}
